import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getGizmosGame, readyGizmosGame, leaveGizmosGame, startGizmosGame, joinGizmosGame } from '../api/gizmos';
import { useGizmosGameWebSocket } from '../hooks/useWebSocket';
import '../styles/pages.css';

export default function GizmosRoomPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [game, setGame] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const lastEventAt = useRef(0);

  const fetchGame = useCallback(async () => {
    try {
      const { data } = await getGizmosGame(id);
      setGame(data);
      if (data.status === 'in_progress') navigate(`/gizmos/${id}/play`);
    } catch {
      setError('获取房间信息失败');
    }
  }, [id, navigate]);

  useEffect(() => {
    fetchGame();
    const t = setInterval(fetchGame, 5000);
    return () => clearInterval(t);
  }, [fetchGame]);

  const handleWsMessage = useCallback((data) => {
    if (data.type === 'room.update' && data.game) {
      const ts = Date.parse(data.event_at || '');
      if (ts && ts < lastEventAt.current) return;
      if (ts) lastEventAt.current = ts;
      setGame(data.game);
      if (data.game.status === 'in_progress') navigate(`/gizmos/${id}/play`);
    }
    if (data.type === 'state.update' && data.game_status === 'in_progress') {
      navigate(`/gizmos/${id}/play`);
    }
  }, [id, navigate]);

  useGizmosGameWebSocket(id, handleWsMessage, { onOpen: fetchGame });

  const myPlayer = game?.players.find(p => p.user === Number(user?.id));
  const isOwner = game?.owner === Number(user?.id);

  const handle = (fn) => async () => {
    setLoading(true); setError('');
    try { await fn(); await fetchGame(); }
    catch (err) { setError(err.response?.data?.detail || '操作失败'); }
    finally { setLoading(false); }
  };

  if (!game) return <div className="loading-page">{error || '加载中...'}</div>;

  return (
    <div className="room-page">
      <div className="room-header">
        <Link to="/gizmos/lobby">← 返回 Gizmos 大厅</Link>
        <h2>Gizmos 房间 #{game.id}</h2>
        <span className={`game-status status-${game.status}`}>{game.status}</span>
      </div>

      {error && <p className="error-text">{error}</p>}

      <div className="players-section">
        <h3>玩家列表 ({game.players.length}/{game.max_players})</h3>
        {game.players.map(p => (
          <div key={p.id} className="player-row">
            <span>{p.username}</span>
            <span className={p.is_ready ? 'ready-badge' : 'not-ready-badge'}>
              {p.is_ready ? '✓ 准备' : '未准备'}
            </span>
          </div>
        ))}
      </div>

      <div className="room-actions">
        {!myPlayer && game.status === 'waiting' && (
          <button className="btn-primary" disabled={loading}
            onClick={handle(() => joinGizmosGame(id))}>
            加入游戏
          </button>
        )}
        {myPlayer && (
          <button className="btn-primary" disabled={loading}
            onClick={handle(() => readyGizmosGame(id))}>
            {myPlayer.is_ready ? '取消准备' : '准备'}
          </button>
        )}
        {isOwner && game.status === 'ready' && (
          <button className="btn-success" disabled={loading}
            onClick={handle(() => startGizmosGame(id))}>
            开始游戏
          </button>
        )}
        {myPlayer && (
          <button className="btn-danger" disabled={loading}
            onClick={handle(() => leaveGizmosGame(id).then(() => navigate('/gizmos/lobby')))}>
            离开
          </button>
        )}
      </div>
    </div>
  );
}
